import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Container, Title, Text, Paper, Group, Badge, SimpleGrid, Box, Loader, Flex, Select } from '@mantine/core';
import { CalendarBlank, User } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';

const CATEGORY_COLORS = {
  'google-ads': 'blue',
  'meta-ads': 'indigo',
  'optymalizacja': 'green',
  'ai': 'brand',
  'e-commerce': 'orange',
  'case-study': 'violet',
  'nowosci': 'cyan',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function PostCard({ post }) {
  return (
    <Paper
      component={Link}
      to={`/blog/${post.slug}`}
      withBorder p="lg" radius="md"
      style={{ textDecoration: 'none', color: 'inherit', transition: 'box-shadow 0.15s, transform 0.15s' }}
      className="campaign-row"
    >
      {post.cover_image && (
        <Box
          mb="sm"
          style={{
            height: 160, borderRadius: 8, overflow: 'hidden',
            background: `url(${post.cover_image}) center/cover`,
          }}
        />
      )}
      <Badge size="xs" variant="light" color={CATEGORY_COLORS[post.category] || 'gray'} mb="xs" styles={{ root: { textTransform: 'none' } }}>
        {post.category_label}
      </Badge>
      <Text size="md" fw={600} mb={6} lh={1.3}>{post.title}</Text>
      <Text size="sm" c="dimmed" lh={1.5} mb="sm" lineClamp={3}>{post.excerpt}</Text>
      <Group gap="md">
        <Group gap={4}>
          <User size={12} className="text-gray-400" />
          <Text size="xs" c="dimmed">{post.author}</Text>
        </Group>
        <Group gap={4}>
          <CalendarBlank size={12} className="text-gray-400" />
          <Text size="xs" c="dimmed">{formatDate(post.published_at)}</Text>
        </Group>
      </Group>
    </Paper>
  );
}

export default function BlogPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);

  useEffect(() => {
    setLoading(true);
    let url = '/accounts/blog/';
    if (category) url += `?category=${category}`;
    api.get(url)
      .then(res => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <>
      <SEO
        title={t('blog.seoTitle')}
        description={t('blog.seoDescription')}
        path="/blog"
      />
      <Navbar />
      <Container size="md" py={48}>
        <Group justify="space-between" align="flex-end" mb="lg">
          <Box>
            <Title order={2}>{t('blog.heading')}</Title>
            <Text c="dimmed" mt={4}>{t('blog.subtitle')}</Text>
          </Box>
          <Select
            placeholder={t('blog.allCategories')}
            data={[
              { value: 'google-ads', label: t('blog.categories.googleAds') },
              { value: 'meta-ads', label: t('blog.categories.metaAds') },
              { value: 'optymalizacja', label: t('blog.categories.optimization') },
              { value: 'ai', label: t('blog.categories.ai') },
              { value: 'e-commerce', label: t('blog.categories.ecommerce') },
              { value: 'case-study', label: t('blog.categories.caseStudy') },
              { value: 'nowosci', label: t('blog.categories.news') },
            ]}
            value={category}
            onChange={setCategory}
            clearable
            size="sm"
            style={{ width: 200 }}
          />
        </Group>

        {loading ? (
          <Flex justify="center" py={60}><Loader color="brand" /></Flex>
        ) : posts.length === 0 ? (
          <Paper withBorder p="xl" radius="md" ta="center">
            <Text size="sm" c="dimmed">{category ? t('blog.noPostsInCategory') : t('blog.noPosts')}</Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {posts.map(post => <PostCard key={post.id} post={post} />)}
          </SimpleGrid>
        )}
      </Container>
      <Footer />
    </>
  );
}
